"use client";

import type React from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import { useEffect, useState } from "react";

interface MonthYearFilterProps {
  onFilter: (filter: string) => void;
  initialMonth?: string;
  initialYear?: string;
}

export const MonthYearFilter: React.FC<MonthYearFilterProps> = ({
  onFilter,
  initialMonth,
  initialYear,
}) => {
  const [month, setMonth] = useState("01");
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [isOpen, setIsOpen] = useState(false);

  // Atualiza os estados quando as props iniciais mudam
  useEffect(() => {
    if (initialMonth && initialYear) {
      setMonth(initialMonth.padStart(2, "0"));
      setYear(initialYear);
    }
  }, [initialMonth, initialYear]);

  const handleFilter = () => {
    if (month && year) {
      onFilter(`${month}/${year}`);
      setIsOpen(false);
    }
  };

  const handleMonthChange = (value: string) => {
    setMonth(value.padStart(2, "0"));
  };

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^\d{0,4}$/.test(value)) {
      setYear(value);
    }
  };

  const toggleFilter = () => {
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative">
      <Button
        variant="outline"
        size="sm"
        onClick={toggleFilter}
        className="bg-white/20 text-white border-white/30 hover:bg-white/30"
      >
        <Calendar className="h-4 w-4 mr-2" />
        Selecionar Período
      </Button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 10 }}
          className="absolute right-0 top-10 mt-2 p-4 bg-white rounded-lg shadow-lg z-10 border border-gray-200 w-[280px]"
        >
          <div className="flex flex-col space-y-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Mês</label>
              <Select value={month} onValueChange={handleMonthChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Mês" />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => {
                    const monthNumber = (i + 1).toString().padStart(2, "0");
                    return (
                      <SelectItem key={monthNumber} value={monthNumber}>
                        {new Date(2000, i).toLocaleString("default", {
                          month: "long",
                        })}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Ano</label>
              <Input
                type="text"
                value={year}
                maxLength={4}
                minLength={4}
                onChange={handleYearChange}
                className="w-full"
                placeholder="Ano"
              />
            </div>

            <div className="flex space-x-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                className="w-1/2"
                onClick={() => setIsOpen(false)}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                className="w-1/2 bg-orange-500 hover:bg-orange-600"
                onClick={handleFilter}
              >
                Aplicar
              </Button>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};
